/**
 * Per-level VisualProfiles composed from presets + level-specific overrides.
 *
 * Each profile is built by spreading a light rig preset and a post-process
 * preset, then adding the level's unique clear color, fog, glow intensity,
 * and named color palette.
 *
 * Palette colors come from the existing GameConfig COLORS and per-level
 * COLORS sections -- they are NOT duplicated here, just referenced by
 * hex string and converted to Color3/Color4 for type safety.
 */
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";

import type { VisualProfile } from "../VisualProfile";

// Light rig presets
import {
  WARM_INTERIOR,
  TWILIGHT_FOREST,
  UNDERGROUND_BAKERY,
  STARLIT_CANOPY,
  DARK_MENU,
} from "./light-rigs";

// Post-process presets
import {
  SOFT_GLOW,
  DENSE_ATMOSPHERE,
  UNDERGROUND_WARMTH,
  CINEMATIC_NIGHT,
} from "./post-process";

// Existing palette constants from GameConfig
import {
  COLORS,
  SCRAPYARD,
  OVERGROWN_CONSERVATORY,
  CLOCKWORK_CANOPY,
} from "../../config/GameConfig";

// ── Title Screen ───────────────────────────────────────────────────────
// Source: TitleScreen.ts
// Minimal dark scene with bioluminescent accents. No fog, no post-process.

export const TITLE_SCREEN_PROFILE: VisualProfile = {
  clearColor: new Color4(0.02, 0.01, 0.05, 1.0),
  lightRig: DARK_MENU,
  postProcess: {},                             // Title screen has no pipeline
  glowIntensity: 0.6,
  palette: {
    deepPurple: Color3.FromHexString(COLORS.DEEP_PURPLE),
    midnightBlue: Color3.FromHexString(COLORS.MIDNIGHT_BLUE),
    bioGreen: Color3.FromHexString(COLORS.BIOLUMINESCENT_GREEN),
    bioPurple: Color3.FromHexString(COLORS.BIOLUMINESCENT_PURPLE),
    brass: Color3.FromHexString(COLORS.BRASS),
    copper: Color3.FromHexString(COLORS.COPPER),
  },
};

// ── Level Select ───────────────────────────────────────────────────────
// Source: LevelSelect.ts
// Very similar to title screen but slightly brighter ambient.

export const LEVEL_SELECT_PROFILE: VisualProfile = {
  clearColor: new Color4(0.03, 0.015, 0.05, 1.0),
  lightRig: {
    ...DARK_MENU,
    ambient: { ...DARK_MENU.ambient, intensity: 0.4 },
  },
  postProcess: {},
  glowIntensity: 0.6,
  palette: {
    deepPurple: Color3.FromHexString(COLORS.DEEP_PURPLE),
    bioGreen: Color3.FromHexString(COLORS.BIOLUMINESCENT_GREEN),
    bioPurple: Color3.FromHexString(COLORS.BIOLUMINESCENT_PURPLE),
    brass: Color3.FromHexString(COLORS.BRASS),
    copper: Color3.FromHexString(COLORS.COPPER),
  },
};

// ── Level 0: Liminal Atrium ────────────────────────────────────────────
// Source: LiminalAtrium.ts
// Cozy Victorian sunroom: warm golden key light, purple fog, soft glow.

export const LIMINAL_ATRIUM_PROFILE: VisualProfile = {
  clearColor: new Color4(0.06, 0.04, 0.09, 1.0),
  fog: {
    color: new Color3(0.08, 0.05, 0.11),
    density: 0.009,
  },
  lightRig: WARM_INTERIOR,
  postProcess: SOFT_GLOW,
  glowIntensity: 0.4,
  palette: {
    deepPurple: Color3.FromHexString(COLORS.DEEP_PURPLE),
    midnightBlue: Color3.FromHexString(COLORS.MIDNIGHT_BLUE),
    brass: Color3.FromHexString(COLORS.BRASS),
    copper: Color3.FromHexString(COLORS.COPPER),
    bioGreen: Color3.FromHexString(COLORS.BIOLUMINESCENT_GREEN),
    ground: Color3.FromHexString(COLORS.GROUND),
    platform: Color3.FromHexString(COLORS.PLATFORM),
  },
};

// ── Level 1: Redwood Scrapyard ─────────────────────────────────────────
// Source: RedwoodScrapyard.ts
// Twilight forest: amber filtered sunlight, dense atmosphere, low fog.

export const REDWOOD_SCRAPYARD_PROFILE: VisualProfile = {
  clearColor: new Color4(0.04, 0.03, 0.09, 1.0),
  fog: {
    color: Color3.FromHexString(SCRAPYARD.COLORS.REDWOOD_DARK),
    density: 0.012,
  },
  lightRig: TWILIGHT_FOREST,
  postProcess: DENSE_ATMOSPHERE,
  glowIntensity: 0.35,
  palette: {
    skyTop: Color3.FromHexString(SCRAPYARD.COLORS.SKY_TOP),
    skyBottom: Color3.FromHexString(SCRAPYARD.COLORS.SKY_BOTTOM),
    redwoodDark: Color3.FromHexString(SCRAPYARD.COLORS.REDWOOD_DARK),
    redwoodBark: Color3.FromHexString(SCRAPYARD.COLORS.REDWOOD_BARK),
    moss: Color3.FromHexString(SCRAPYARD.COLORS.MOSS),
    rust: Color3.FromHexString(SCRAPYARD.COLORS.RUST),
    brass: Color3.FromHexString(COLORS.BRASS),
    bioGreen: Color3.FromHexString(COLORS.BIOLUMINESCENT_GREEN),
    deepPurple: Color3.FromHexString(COLORS.DEEP_PURPLE),
  },
};

// ── Level 2: Overgrown Conservatory ────────────────────────────────────
// Source: OvergrownConservatory.ts
// Underground bakery: warm oven glow, candle accents, brown vignette.

export const OVERGROWN_CONSERVATORY_PROFILE: VisualProfile = {
  // Canon (book p.16-19): Victorian glass dome overrun with
  // bioluminescent flora + boiler heat. Neither "brown burrow" nor
  // "pitch black warren" — the shipped profile inherited both from
  // the discarded Midnight Burrow draft and drowned the new
  // Meshy-authored vines/cakes/ferns in dark brown fog. Gemini's
  // fluidity review flagged the resulting scene as "a solid
  // brownish-red block obscuring the environment."
  //
  // New values:
  //   clearColor — deep teal with a hint of warm undertone so the
  //     Boiler-Bound Sundew's orange glow at the east reads against
  //     cold mist at the west.
  //   fog color — cool conservatory mist (muted teal/green) instead
  //     of brown earth.
  //   fog density — halved so the Meshy vines, petrified cakes, and
  //     bioluminescent ferns are actually visible past 10 units.
  clearColor: new Color4(0.07, 0.11, 0.14, 1.0),
  fog: {
    // Muted teal conservatory mist. Density 0.006 is half the
    // original burrow value — lets the Meshy vines, petrified cakes,
    // and bioluminescent ferns read past 15 world units without
    // erasing the atmospheric depth entirely.
    //
    // 2026-07 cool-teal correction (Q-039): the prior (0.22, 0.38,
    // 0.32) was green-dominant (G > B) and read as green haze, not the
    // brief's cool teal. Blue now leads green at similar luminance.
    color: new Color3(0.16, 0.33, 0.38),
    density: 0.006,
  },
  lightRig: UNDERGROUND_BAKERY,
  postProcess: UNDERGROUND_WARMTH,
  glowIntensity: 0.4,
  palette: {
    amberWarm: Color3.FromHexString(OVERGROWN_CONSERVATORY.COLORS.AMBER_WARM),
    kitchenCopper: Color3.FromHexString(OVERGROWN_CONSERVATORY.COLORS.KITCHEN_COPPER),
    burrowEarth: Color3.FromHexString(OVERGROWN_CONSERVATORY.COLORS.BURROW_EARTH),
    burrowDeep: Color3.FromHexString(OVERGROWN_CONSERVATORY.COLORS.BURROW_DEEP),
    midnightIndigo: Color3.FromHexString(OVERGROWN_CONSERVATORY.COLORS.MIDNIGHT_INDIGO),
    midnightDeep: Color3.FromHexString(OVERGROWN_CONSERVATORY.COLORS.MIDNIGHT_DEEP),
    brass: Color3.FromHexString(COLORS.BRASS),
    copper: Color3.FromHexString(COLORS.COPPER),
    bioGreen: Color3.FromHexString(COLORS.BIOLUMINESCENT_GREEN),
  },
};

// ── Level 3: Clockwork Canopy ──────────────────────────────────────────
// Source: ClockworkCanopy.ts
// Starlit treetops: cool moonlight, brass accents, cinematic vignette.

export const CLOCKWORK_CANOPY_PROFILE: VisualProfile = {
  clearColor: new Color4(0.02, 0.04, 0.1, 1.0),
  fog: {
    color: Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.GOLD_FOG),
    density: 0.008,
  },
  lightRig: STARLIT_CANOPY,
  postProcess: CINEMATIC_NIGHT,
  glowIntensity: 0.45,
  palette: {
    starlitSky: Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.STARLIT_SKY),
    midnightBlue: Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.MIDNIGHT_BLUE),
    brassWarm: Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.BRASS_WARM),
    goldFog: Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.GOLD_FOG),
    copperDark: Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.COPPER_DARK),
    lensRed: Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.LENS_RED),
    lensGreen: Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.LENS_GREEN),
    lensBlue: Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.LENS_BLUE),
    lensGold: Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.LENS_GOLD),
    constellation: Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.CONSTELLATION),
    orreryBrass: Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.ORRERY_BRASS),
    bioBlue: Color3.FromHexString(COLORS.BIOLUMINESCENT_BLUE),
    brass: Color3.FromHexString(COLORS.BRASS),
  },
};
